import { createSelector } from 'reselect'
import config from 'constants/config'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { runChannelMarkupV2SessionRound } from 'lib/channelMarkup/channelMarkupV2'
import type { ChannelMarkupSource, ChannelMarkupV2SessionContext } from 'lib/channelMarkup/channelMarkupV2'

export const getChannelMarkup = (state: App.State) => config.ENABLE_CHANNEL_MARKUP ? state.marketing.channelMarkup : undefined

/**
 * The channel markup v2 session (user + market) context used by `getChannelMarkupSource`
 * to run the session round. Memoized on its primitive inputs so it can be composed as an
 * input selector without breaking `getChannelMarkupSource`'s memoization.
 */
export const getChannelMarkupV2SessionContext = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => state.geo.currentCurrency,
  // Subscriber tier only carries meaning for a member who can currently view LuxPlus+
  // benefits; everyone else evaluates as `null`.
  (state: App.State) => (getCanViewLuxPlusBenefits(state) ? (state.auth.account.luxPlus.member.subscription.item?.tier ?? null) : null),
  (state: App.State) => state.auth.account.roles,
  (region, currency, subscriberTier, roles): ChannelMarkupV2SessionContext => ({ region, currency, subscriberTier, roles }),
)

/**
 * The mode-tagged markup source to thread through the offer mappers. Returns the v2
 * session state (evaluated once from the client-init config) when channel markup v2 is
 * active and its config is available; otherwise falls back to the v1 `channelMarkup`.
 *
 * Memoized: the v2 session round rebuilds a fresh object on every call, so reactive
 * `useAppSelector` consumers need a stable reference to avoid re-rendering on every store
 * update. It recomputes only when its inputs (config, region, currency, tier, roles, v1
 * markup) actually change.
 */
export const getChannelMarkupSource = createSelector(
  (state: App.State) => state.marketing.clientInitConfig?.channelMarkupInitConfig,
  (state: App.State) => getChannelMarkupV2SessionContext(state),
  (state: App.State) => getChannelMarkup(state),
  (channelMarkupConfig, sessionContext, channelMarkup): ChannelMarkupSource => {
    const isV2Active = config.ENABLE_CHANNEL_MARKUP
      && config.CHANNEL_MARKUP_EVALUATION_MODE === 'v2'
      && !!channelMarkupConfig
      && !!sessionContext.region
      && !!sessionContext.currency

    if (isV2Active) {
      const { sessionState, channelMarkupId } = runChannelMarkupV2SessionRound(channelMarkupConfig, sessionContext)
      return { mode: 'v2', sessionState, blockList: channelMarkupConfig.globalOfferIdBlockList ?? [], channelMarkupId }
    }

    return { mode: 'v1', channelMarkup }
  },
)
